import { styled } from "styled-components";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import gfm from "remark-gfm";
import { Tooltip } from "antd";
import {
  CheckMarkSimpleIcon,
  ClipboardIcon,
  HandThumbsDownIcon,
  HandThumbsUpIcon,
  OneGateLogo_1Icon,
} from "@dataone/icons";
import { scrollBarStyles } from "@dataone/lib";

interface ChatResponseProps {
  message: string;
}

const ChatResponse = ({ message }: ChatResponseProps) => {
  const [copied, setCopied] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isDisLiked, setIsDisLiked] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };
  const handleLike = () => {
    setIsLiked(!isLiked);
    isDisLiked && setIsDisLiked(false);
  };
  const handleDisLike = () => {
    setIsDisLiked(!isDisLiked);
    isLiked && setIsLiked(false);
  };
  return (
    <StyledContainer>
      <DefaultDiv>
        <StyledOnegateLogo1Icon>
          <OneGateLogo_1Icon />
        </StyledOnegateLogo1Icon>
        <StyledContent>
          <ReactMarkdown
            children={message}
            remarkPlugins={[gfm]}
            components={{
              code({ node, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                return match ? (
                  <SyntaxHighlighter language={match[1]}>
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          />
        </StyledContent>
      </DefaultDiv>
      <div className="text-footer">
        <Tooltip
          placement="bottom"
          title="Wrong Answer"
          overlayStyle={{ maxWidth: "300px", fontSize: "12px" }}
        >
          <StyledHandDownIcon onClick={handleDisLike} isDisLiked={isDisLiked}>
            <HandThumbsDownIcon />
          </StyledHandDownIcon>
        </Tooltip>
        <Tooltip
          placement="bottom"
          title="Good Answer"
          overlayStyle={{ maxWidth: "300px", fontSize: "12px" }}
        >
          <StyledHandUpIcon onClick={handleLike} isLiked={isLiked}>
            <HandThumbsUpIcon />
          </StyledHandUpIcon>
        </Tooltip>
        <Tooltip
          placement="bottom"
          title="Copy"
          overlayStyle={{ maxWidth: "300px", fontSize: "12px" }}
        >
          <StyleCopiedIcon onClick={handleCopy} copied={copied}>
            {copied ? <CheckMarkSimpleIcon /> : <ClipboardIcon />}
          </StyleCopiedIcon>
        </Tooltip>
      </div>
    </StyledContainer>
  );
};

export default ChatResponse;

const DefaultDiv = styled.div`
  margin-right: 10px;
  display: flex;
  flex-direction: row;
`;

const StyledContent = styled.div`
  color: #040726;
  font-size: 13px;
  font-weight: 400;
  line-height: 30px;
  letter-spacing: -0.3px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  background: #fff;
  padding: 10px;
  overflow-x: auto;
  ${scrollBarStyles}
  .text-footer {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    color: rgba(4, 7, 38, 0.6);
    font-size: 14px;
    font-weight: 400;
    margin-top: -10px;
    .icon {
      cursor: pointer;
    }
  }
  .text-footer img {
    padding: 10px;
    width: 33px;
  }
`;

const StyledOnegateLogo1Icon = styled.div`
  display: flex;
  margin-right: 15px;
  margin-top: 17px;
  font-size: 18px;
  color: #4974f6;
`;

const StyleCopiedIcon = styled.div<{ copied: boolean }>`
  cursor: pointer;
  font-size: ${({ copied }) => (copied ? "25px" : "15px")};
  color: #040726;
  display: flex;
  // margin-bottom: 4px;
  width: 30px;
`;

const StyledHandUpIcon = styled.div<{ isLiked: boolean }>`
  cursor: pointer;
  display: flex;
  width: 30px;
  color: ${({ isLiked }) => (isLiked ? "#00FF00" : "#040726")};
`;

const StyledHandDownIcon = styled.div<{ isDisLiked: boolean }>`
  cursor: pointer;
  display: flex;
  width: 30px;
  color: ${({ isDisLiked }) => (isDisLiked ? "#FF0000" : "#040726")};
`;
